import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PosterArt from "@/components/media/PosterArt";
import ContentCard from "@/components/media/ContentCard";
import ContentRow from "@/components/media/ContentRow";
import SEO from "@/components/seo/SEO";
import { Share2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import WatchNowButton from "@/components/watch/WatchNowButton";

export default function TitleDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [title, setTitle] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setTitle(null);
    api.get(`/titles/${slug}`).then(({ data }) => {
      setTitle(data);
      api.get(`/titles?type=${data.type}&limit=12`).then(({ data: rel }) => {
        setRelated(rel.filter((r) => r.slug !== data.slug).slice(0, 10));
      });
    }).catch(() => nav("/"));
  }, [slug, nav]);

  if (!title) return <div className="min-h-screen flex items-center justify-center pt-20 text-white/40">Loading…</div>;

  // SEO: use official synopsis when available; otherwise a factual template (no invented content)
  const typeLabel = title.is_original ? "YCN Original" : (title.type ? title.type.charAt(0).toUpperCase() + title.type.slice(1) : "Title");
  const seoDesc = title.description
    ? `${title.description.slice(0, 155)}${title.description.length > 155 ? "…" : ""}`
    : `Watch ${title.title} on Your Channel Network — ${typeLabel}${title.category ? ` · ${title.category}` : ""}. Stream original programming and independent storytelling on YCN.`;

  return (
    <div data-testid="title-detail-page" className="pt-16 md:pt-20">
      <SEO
        title={title.title}
        description={seoDesc}
        path={`/title/${title.slug}`}
        image={title.backdrop_url || title.poster_url || undefined}
      />
      {/* Backdrop */}
      <div className="relative h-[60vh] min-h-[420px] md:h-[75vh] w-full overflow-hidden">
        {title.backdrop_url ? (
          <img src={title.backdrop_url} alt={title.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <PosterArt gradient={title.backdrop_gradient || title.poster_gradient} aspect="backdrop" showTitle={false} className="!aspect-auto h-full w-full" overlayIntensity={0} />
        )}<div className="absolute inset-0 ycn-hero-vignette" />
        <button onClick={() => nav(-1)} className="absolute top-6 left-6 z-20 h-10 w-10 rounded-full ycn-glass flex items-center justify-center text-white/80 hover:text-white">
          <ArrowLeft size={18} />
        </button>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-[1400px] w-full mx-auto px-6 md:px-12 pb-10 md:pb-16">
            <div className="grid md:grid-cols-[220px,1fr] gap-8 items-end">
              <div className="hidden md:block rounded-xl overflow-hidden shadow-2xl border border-white/10">
                {title.poster_url ? (
                  <img src={title.poster_url} alt={title.title} className="w-full h-auto block" />
                ) : (
                  <PosterArt title={title.title} gradient={title.poster_gradient} subtitle={title.is_original ? "ORIGINAL" : title.type?.toUpperCase()} />
                )}
              </div>
              <div className="max-w-2xl">
                {title.is_original && (
                  <div className="mb-3 inline-flex items-center gap-2 px-3 py-1 rounded-full ycn-glass text-[11px] uppercase tracking-[0.25em]">
                    <span className="font-bold ycn-gradient-text">YCN Original</span>
                  </div>
                )}
                <h1 className="font-display text-5xl md:text-7xl font-black uppercase leading-[0.95] tracking-tight">{title.title}</h1>
                <div className="mt-4 flex items-center gap-3 text-sm text-white/70 flex-wrap">
                  {title.year && <span>{title.year}</span>}
                  {title.rating && <span className="px-1.5 py-0.5 border border-white/20 rounded text-xs">{title.rating}</span>}
                  {title.duration && <span>{title.duration}</span>}
                  {title.genres?.map((g) => <span key={g} className="text-white/60">• {g}</span>)}
                </div>
                <p className="mt-5 text-base md:text-lg text-white/85 leading-relaxed">{title.description}</p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <WatchNowButton testId="detail-play-btn" label="Play" className="px-7 py-3 text-base" />
                  <button data-testid="detail-share-btn" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied"); }} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 hover:bg-white/10 transition">
                    <Share2 size={16} /> Share
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <ContentRow title="More Like This" testId="row-related">
          {related.map((t) => (
            <div key={t.id} className="w-[170px] md:w-[200px] shrink-0"><ContentCard title={t} /></div>
          ))}
        </ContentRow>
      )}
      <div className="h-16" />
    </div>
  );
}
